import React from 'react';

import { Divider } from '@mui/material';

import { SearchView } from '@kamernet/core/ApiClient/Kamernet';
import { AuthContext } from '@kamernet/core/Auth/AuthContext';
import { PageLayout } from '@kamernet/core/PageLayout';
import { CommonStyles } from '@kamernet/styles/CommonStyles';

import { isCitySearch } from '@kamernet/modules/Search/utilities';

import { ListingsSearchContext } from '../../ListingsSearchContext';
import { Content } from '../Content';

import { Breadcrumbs } from './Breadcrumbs';
import { FeaturedListings } from './FeaturedListings';
import { GridGenerator } from './GridGenerator';
import { HAListingsBanner } from './HAListingsBanner/HAListingsBanner';
import styles from './ListingsGrid.module.css';
import { NearbyListings } from './NearbyListings';
import { NoListingsMessage } from './NoListingsMessage/NoListingsMessage';
import { Pagination } from './Pagination/Pagination';
import { SearchAlertBanner } from './SearchAlertBanner/SearchAlertBanner';
import { SearchUrgencyMessage } from './SearchUrgencyMessage';
import { SortSelect } from './SortSelect';

export interface ListingsGridProps {
  className?: string;
}

export const ListingsGrid = ({ className = undefined }: ListingsGridProps) => {
  const { listings, topListings, allFilters, nearbyListings } =
    ListingsSearchContext.useContext();

  const beforeFeaturedListing = listings ? listings?.slice(0, 6) : [];
  const afterFeaturedListing =
    listings && listings?.length > 6 ? listings?.slice(6) : [];

  const { isAuthenticated } = AuthContext.useContext();

  const showSignupCard =
    !isAuthenticated &&
    isCitySearch(allFilters) &&
    afterFeaturedListing?.length > 0;

  const showSignupBanner =
    !isAuthenticated &&
    isCitySearch(allFilters) &&
    !afterFeaturedListing?.length;

  return (
    <PageLayout.SectionContainer
      className={className}
      containerClassName={styles.root}
    >
      <SearchUrgencyMessage />
      <SortSelect />

      {beforeFeaturedListing.length > 0 && (
        <React.Fragment>
          <GridGenerator listings={beforeFeaturedListing} />
          <Divider className={CommonStyles.margin_bottom_3} />
        </React.Fragment>
      )}
      {allFilters.searchView !== SearchView.Map && <FeaturedListings />}

      {afterFeaturedListing.length > 0 && (
        <React.Fragment>
          <GridGenerator
            listings={afterFeaturedListing}
            addSignupCard={showSignupCard}
          />
          <Divider className={CommonStyles.margin_bottom_3} />
        </React.Fragment>
      )}

      <NearbyListings />

      {!listings?.length && !topListings?.length && !nearbyListings?.length && (
        <NoListingsMessage />
      )}

      {showSignupBanner && <SearchAlertBanner />}

      <Pagination />

      {allFilters.searchView !== SearchView.Map && (
        <div className={CommonStyles.margin_top_5}>
          <nav className={styles.breadcrumbs}>
            <Breadcrumbs />
          </nav>

          <HAListingsBanner />
          <div
            data-testid="content-container"
            className={CommonStyles.margin_bottom_5}
          >
            <Content />
          </div>
        </div>
      )}
    </PageLayout.SectionContainer>
  );
};
